import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom'
import { Alert } from 'react-bootstrap';

import SharePointFolderBrowser from './psc/lib/SharePointFolderBrowser/SharePointFolderBrowser';
import SaveButton from './psc/lib/SaveButton';
import CancelButton from './psc/lib/CancelButton';
import ErrorMessage from './psc/lib/ErrorMessage';
import Checkbox from './psc/lib/Checkbox';

import useAuthentication from '../hooks/useAuthentication';
import useSharePointToken from '../hooks/useSharePointToken';
import useUserManagement from '../hooks/useUserManagement';
import useBrowserTabTitle from '../hooks/useBrowserTabTitle';

const SharePointConfiguration = ({
  title,
  pageTitle,
  apiUrl,
  typesKey,
  redirectPath,
  adminRole,
  readRole,
  contentTypeName,
  contentTypeNamePlural,
  noTypesMessage,
  defaultLocationHelpText,
  typeLocationsLabel = 'Type Locations',
}) => {
  const [showFolderBrowser, setShowFolderBrowser] = useState(false);
  const [types, setTypes] = useState([]);
  const [editingType, setEditingType] = useState(null);
  const [defaultSharePointFolder, setDefaultSharePointFolder] = useState(null);
  const [isUsingSharePointIntegration, setIsUsingSharePointIntegration] = useState(false);
  const [error, setError] = useState(null);
  const [submitSpinner, setSubmitSpinner] = useState(false);
  const [submitActive, setSubmitActive] = useState(false);
  const [authenticateSpinner, setAuthenticateSpinner] = useState(false);
  const [configLoaded, setConfigLoaded] = useState(false);

  const navigate = useNavigate()

  useBrowserTabTitle(pageTitle)

  const { currentUser } = useAuthentication();

  const {
    tokenIsValid,
    redirectToAuthUrl,
    encryptionKey,
    loading,
    sharePointApplicationIsValid,
    sharePointApplicationError,
  } = useSharePointToken();

  const {
      userHasRole,
  } = useUserManagement()

  useEffect(() => {
    fetch(apiUrl, { credentials: 'same-origin' })
      .then(response => {
        if (response.ok) {
          return response.json()
        }
        throw new Error('Network response was not ok.')
      })
      .then(json => {
        const config = json.result
        setIsUsingSharePointIntegration(config.is_using_sharepoint_integration)
        if (config.default_sharepoint_folder && config.default_sharepoint_folder.id > 0) {
          setDefaultSharePointFolder(config.default_sharepoint_folder)
        }
        if (config[typesKey] && config[typesKey].length > 0) {
          setTypes(config[typesKey])
        }
        setConfigLoaded(true)
      })
      .catch(error => {
        console.error('Error loading SharePoint config:', error.message)
      })
  }, [apiUrl, typesKey])

  const handleAuthenticate = () => {
    setAuthenticateSpinner(true);
    redirectToAuthUrl();
  };

  const handleFolderSelect = ({ sharepointFolder }) => {
    if (editingType) {
      const updated = types.map((type) => {
        if (type.id === editingType.id) {
          return { ...type, sharepoint_folder: sharepointFolder }
        }
        return type
      })
      setTypes(updated)
      setEditingType(null);
    } else {
      setDefaultSharePointFolder(sharepointFolder);
    }
    setShowFolderBrowser(false);
  };

  const handleChangeFolder = (type) => {
    setEditingType(type);
    setShowFolderBrowser(true);
  };

  const handleClearFolder = (type) => {
    const updated = types.map((t) => {
      if (t.id === type.id) {
        return { ...t, sharepoint_folder: null }
      }
      return t
    })
    setTypes(updated);
  };

  const handleSubmitError = (error) => {
    setError(error);
    setSubmitSpinner(false);
  };

  const renderType = (type) => {
    const pathIsDefined = type.sharepoint_folder && type.sharepoint_folder?.site_name !== "" && type.sharepoint_folder?.file_path !== ""
    const siteName = type.sharepoint_folder?.site_name || 'Not set';
    const folderPath = type.sharepoint_folder?.file_path || 'Not set';
    const locationText = `${siteName} | ${folderPath}`;

    return (
      <div className="product-tree-item" key={type.id} style={{ gridColumn: '1 / -1', width: '100%' }}>
        <div className="product-tree-name">
          <span className="product-tree-name-text">{type.name}</span>
        </div>
        <div className="product-tree-sharepoint">
          <div className="sharepoint-location">
            <span {...(pathIsDefined ? { 'data-tooltip-id': 'gnctooltip', 'data-tooltip-content': locationText } : {})} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 'calc(100% - 30px)' }}>{locationText}</span>
              <button
                type="button"
                className={`btn btn-xs btn-link sharepoint-clear-btn${pathIsDefined ? '' : ' invisible'}`}
                onClick={() => handleClearFolder(type)}
                style={{ marginLeft: 'auto', padding: '0 4px', color: 'rgb(204, 204, 204)', visibility: pathIsDefined ? 'visible' : 'hidden' }}
              >
                <i className="fa fa-times" aria-hidden="true"></i>
              </button>
            </div>
          <button
            type="button"
            className="btn btn-xs btn-primary"
            onClick={() => handleChangeFolder(type)}
          >
            Change
          </button>
        </div>
      </div>
    );
  };

  const handleSubmit = (event) => {
    if (event) {
      event.preventDefault();
    }
    if (submitActive || !validateBeforeSubmit()) {
      return null;
    }

    setSubmitActive(true);
    setSubmitSpinner(true);

    const typesData = types.map(type => ({
      id: type.id,
      name: type.name,
      sharepoint_folder: type.sharepoint_folder,
    }));

    const data = {
      is_using_sharepoint_integration: isUsingSharePointIntegration,
      default_sharepoint_folder: defaultSharePointFolder,
      [typesKey]: typesData
    }

    fetch(apiUrl, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        }).then(response => {
            if (response.ok) {
                return response.json()
            }
            throw new Error('Network response was not ok.');
        }).then(json => {
            navigate(redirectPath)
        }).catch(error => {
            console.error('There has been a problem with your fetch operation:', error.message);
        });
    return null
  };

  const validateBeforeSubmit = () => {
    let error = []
    if ( isUsingSharePointIntegration && ! defaultSharePointFolder ) {
        error.push("Default Location is required")
    }
    if ( error.length > 0 ) {
        setError(error)
        return false
    }
    return true
  }

  if (loading || !configLoaded) {
    return (
      <div className="wrapper wrapper-content">
        <div className="row">
          <div className="col-lg-12">
            <div className="ibox float-e-margins">
              <div className="ibox-content">
                <div>Loading...</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!sharePointApplicationIsValid) {
    return (
      <div className="wrapper wrapper-content">
        <div className="row">
          <div className="col-lg-12">
            <div className="ibox float-e-margins">
              <div className="ibox-title">
                <h2>SharePoint integration not yet configured</h2>
              </div>
              <div className="ibox-content">
                <Alert variant="warning">
                  In order to use SharePoint, a CADM Admin first needs to configure the SharePoint Application.
                </Alert>
                <p>
                  Error given when trying to validate the SharePoint Application: {sharePointApplicationError}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!tokenIsValid) {
    return (
      <div className="wrapper wrapper-content">
        <div className="row">
          <div className="col-lg-12">
            <div className="ibox float-e-margins">
              <div className="ibox-title">
                <h2>SharePoint</h2>
              </div>
              <div className="ibox-content">
                <Alert variant="warning">
                  <p><strong>Please Authenticate to begin</strong></p>

                  <p>SharePoint integration has been enabled by your CADM Admin.</p>

                  <p>This area allows you to activate and configure SharePoint integration for {contentTypeNamePlural}.</p>
                </Alert>
                <button
                  className={`btn btn-mr has-spinner btn-primary${authenticateSpinner ? ' active' : ''}`}
                  role="button"
                  onClick={() => handleAuthenticate() }
                >
                  <span className="psc-spinner"><i className="fa fa-refresh fa-spin fa-fw"></i></span>
                  Authenticate via SharePoint
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const defaultFolderIsDefined = !!defaultSharePointFolder
  const defaultFolderText =  defaultFolderIsDefined ? `${defaultSharePointFolder.site_name} | ${defaultSharePointFolder.file_path}` : "";

  return (
    <div className="wrapper wrapper-content">
      <div className="row">
        <div className="col-lg-12">
          <div className="ibox float-e-margins">
            <div className="ibox-title">
              <h2>{title}</h2>
              <div key="newText1" style={{color: 'rgba(103, 106, 108, 0.65)'}}>
                <p>
                  The <strong>GNC Go SharePoint Integration</strong> creates a limited connection between GNC Go and your SharePoint sites, allowing automation of certain tasks.
                </p>
                <ul>
                  <li>
                    Set a <strong>Default Location</strong> as the fallback for all {contentTypeNamePlural.toLowerCase()}.
                  </li>
                  <li>
                    Optionally, you can auto-file {contentTypeNamePlural.toLowerCase()} by <strong>{contentTypeName} Type</strong>, by assigning SharePoint locations to each <strong>{contentTypeName} Type</strong>.
                    When a {contentTypeName.toLowerCase()} is created, GNC Go will upload it to the location defined for its {contentTypeName} Type.
                    If none is found, the Default Location is used.
                  </li>
                </ul>
              </div>
            </div>
            <div className="ibox-content sharepoint">
              <form className="form-horizontal" onSubmit={handleSubmit}>
                <div className="form-group">
                  <label className="col-sm-2 control-label">Use SharePoint Integration</label>
                  <div className="col-sm-8" style={{ paddingTop: '7px' }}>
                    <Checkbox
                      isChecked={isUsingSharePointIntegration}
                      onChange={(e) => setIsUsingSharePointIntegration(e.target.checked)}
                    />
                    <span className="help-block m-b-none m-t-none" >
                      <small className="form-info-text" > Changes may take a few minutes to reach all users. </small>
                    </span>
                  </div>
                </div>

                <div className="hr-line-dashed"></div>

                <div className="form-group" style={!isUsingSharePointIntegration ? { opacity: 0.5 } : {}}>
                  <label className={`col-sm-2 control-label${isUsingSharePointIntegration ? ' required' : ''}`}>Default Location</label>
                  <div className="col-sm-8">
                    <div style={{ display: 'flex' }}>
                      <button
                        type="button"
                        className="btn btn-mr btn-primary"
                        style={{
                          whiteSpace: 'nowrap',
                          borderTopRightRadius: 0,
                          borderBottomRightRadius: 0,
                          margin: 0,
                          height: '34px',
                          lineHeight: '0.5'
                        }}
                        onClick={() => setShowFolderBrowser(true)}
                        disabled={!isUsingSharePointIntegration}
                      >
                        Change
                      </button>
                      <input
                        type="text"
                        className="form-control"
                        value={defaultFolderText}
                        data-tooltip-id="gnctooltip"
                        data-tooltip-content={defaultFolderText}
                        disabled
                        style={{
                          flex: 1,
                          borderTopLeftRadius: 0,
                          borderBottomLeftRadius: 0,
                          marginLeft: '-1px',
                          height: '34px',
                          padding: '6px 12px'
                        }}
                      />
                    </div>
                    <span className="help-block m-b-none m-t-none" >
                      <small className="form-info-text" > {defaultLocationHelpText} </small>
                    </span>
                  </div>
                </div>

                <div className="hr-line-dashed"></div>

                <div className="form-group" style={!isUsingSharePointIntegration ? { opacity: 0.5, pointerEvents: 'none' } : {}}>
                  <label className="col-sm-2 control-label">{typeLocationsLabel}</label>
                  <div className="col-sm-8">
                    {types.length > 0 ? (
                      <div className="product-tree-container">
                        {types.map(renderType)}
                      </div>
                    ) : (
                       <div className="alert alert-info">{noTypesMessage}</div>
                    )}
                  </div>
                </div>

                <div className="hr-line-dashed"></div>

                <div className="form-group">
                  <ErrorMessage
                    error={error}
                  />
                  <div className="col-sm-10 col-sm-offset-2">
                    <CancelButton
                        navigateOverride={() => navigate(redirectPath)}
                    />
                    <SaveButton
                        buttonVariant='primary'
                        visible={userHasRole(currentUser, adminRole)}
                        spinnerActive={submitSpinner}
                        label='Save Changes'
                        onClick={handleSubmit}
                        onError={handleSubmitError}
                    />
                  </div>
                </div>
              </form>

              <SharePointFolderBrowser
                show={showFolderBrowser}
                onClose={() => setShowFolderBrowser(false)}
                onSelect={handleFolderSelect}
                encryptionKey={encryptionKey}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SharePointConfiguration;
